// JSON-LD builders for schema.org structured data.
//
// Centralised so we can update fields once (e.g. sameAs, brand name) and have
// them propagate across the marketing site. Builders return plain objects;
// call `jsonLdScript(obj)` to produce the `{ type, children }` shape that
// TanStack Router's `head.scripts` expects.

const BASE_URL = 'https://uptrack.app'
const LOGO_URL = `${BASE_URL}/uptrack-logo-header.png`

export const ORG_SAME_AS = [
  'https://x.com/UptrackDotApp',
  'https://github.com/Uptrack-App',
  'https://www.linkedin.com/company/uptrack-app',
  'https://bsky.app/profile/uptrack.app',
]

const PUBLISHER = {
  '@type': 'Organization',
  name: 'Uptrack',
  url: BASE_URL,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
}

type JsonLd = Record<string, unknown>

export function jsonLdScript(obj: JsonLd): { type: 'application/ld+json'; children: string } {
  // `<` is escaped so no value can close the script element (`</script>`) or open a comment.
  return { type: 'application/ld+json', children: JSON.stringify(obj).replace(/</g, '\\u003c') }
}

export function organization(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Uptrack',
    url: BASE_URL,
    logo: LOGO_URL,
    sameAs: ORG_SAME_AS,
  }
}

export interface WebPageInput {
  name: string
  description: string
  /** The canonical URL of the page. */
  url: string
  /** `CollectionPage` for a list page; `WebPage` for the rest. */
  type?: 'WebPage' | 'CollectionPage'
}

/**
 * A plain page: the right type for a feature page, a tool, a comparison or the home page.
 * It carries no rating or review property, so it needs none. Use `article()` only for a blog
 * post or a guide.
 */
export function webPage(input: WebPageInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': input.type ?? 'WebPage',
    name: input.name,
    description: input.description,
    url: input.url,
    isPartOf: { '@type': 'WebSite', name: 'Uptrack', url: BASE_URL },
  }
}

export interface FaqEntry {
  question: string
  answer: string
}

export function faqPage(entries: FaqEntry[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((e) => ({
      '@type': 'Question',
      name: e.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: e.answer.replace(/\n/g, ' '),
      },
    })),
  }
}

export interface ItemListEntry {
  name: string
  url: string
  description?: string
}

export function itemList(name: string, items: ItemListEntry[], listUrl?: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    ...(listUrl ? { url: listUrl } : {}),
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
      ...(item.description ? { description: item.description } : {}),
    })),
  }
}

export interface HowToStepInput {
  name: string
  text: string
}

export interface HowToInput {
  name: string
  description: string
  url: string
  steps: HowToStepInput[]
}

export function howTo(input: HowToInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: input.name,
    description: input.description,
    url: input.url,
    step: input.steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  }
}

export interface BreadcrumbItem {
  name: string
  url?: string
}

export function breadcrumbList(items: BreadcrumbItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => {
      const node: Record<string, unknown> = {
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
      }
      // Final crumb omits `item` per Google's spec — it represents the current page.
      if (item.url && i < items.length - 1) {
        node.item = item.url
      }
      return node
    }),
  }
}

export interface ArticleInput {
  headline: string
  description: string
  url: string
  datePublished: string
  dateModified?: string
  imageUrl?: string
}

// Article schema with required freshness fields. AI search engines (Gemini AI
// Mode, AI Overviews) use `dateModified` as the primary tie-breaker when
// multiple sources cover the same topic, and require `author` + `publisher`
// + `datePublished` for full credit.
export function article(input: ArticleInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.headline,
    description: input.description,
    url: input.url,
    mainEntityOfPage: input.url,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: PUBLISHER,
    publisher: PUBLISHER,
    ...(input.imageUrl ? { image: input.imageUrl } : {}),
  }
}

export interface OfferInput {
  name: string
  price: string
  priceCurrency?: string
  description: string
  billingDuration?: 'P1M' | 'P1Y'
  priceValidUntil?: string
  url?: string
}

const DEFAULT_PRICE_VALID_UNTIL = '2027-12-31'

export function offer(input: OfferInput): JsonLd {
  const o: JsonLd = {
    '@type': 'Offer',
    name: input.name,
    price: input.price,
    priceCurrency: input.priceCurrency ?? 'USD',
    description: input.description,
    priceValidUntil: input.priceValidUntil ?? DEFAULT_PRICE_VALID_UNTIL,
    ...(input.url ? { url: input.url } : {}),
  }
  if (input.billingDuration) {
    o.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      price: input.price,
      priceCurrency: input.priceCurrency ?? 'USD',
      billingDuration: input.billingDuration,
      unitCode: 'MON',
    }
  }
  return o
}
